import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useDataProvider,
  useNotify,
  useStore,
  useTranslate
} from 'react-admin';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

// Import from common_modules
import {
  Payment,
  PaymentTotal,
  formatCurrency,
  PaymentScheduleTable,
  PaymentTotalSummary,
  PaymentDetailsList,
  PaymentDetailItemsList
} from '../common_modules/components/payments';

const CustomListNoResults = () => {
  return (
    <Box textAlign="center">
      <InboxIcon sx={{ fontSize: 216, color: '#9e9e9e' }} />
      <Typography variant="h4" color="#9e9e9e" mt={2}>
        支払い状況データはありません
      </Typography>
    </Box>
  );
};

// Payment detail page component
export const PartnerPaymentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const paymentId = parseInt(id || '0', 10);
  const dataProvider = useDataProvider();
  const [carryoverAmount, setCarryoverAmount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        setLoading(true);
        const groupPaymentId = searchParams.get('paymentId');
        const result = await dataProvider.getList(`payments/details/${groupPaymentId}`, {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'id', order: 'ASC' },
          filter: {
            id: Number(id)
          }
        });
        if (result.data.length > 0) {
          setCarryoverAmount(result.data[0].rawCarryoverAmount);
        } else {
          setCarryoverAmount(0);
        }
      } catch (error) {
        console.error('Failed to fetch payment details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (paymentId) {
      fetchPaymentDetails();
    }
  }, [paymentId, dataProvider, searchParams, id]);

  const handleBack = () => {
    const paymentIdParam = searchParams.get('paymentId');
    const queryString = paymentIdParam ? `?paymentId=${paymentIdParam}` : '';
    navigate(`/partner-payments${queryString}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">報酬の支払状況詳細</Typography>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1">
          {loading ? <CircularProgress size={20} /> : '前月分までの繰越金額: ' + formatCurrency(carryoverAmount)}
        </Typography>
      </Box>
      <PaymentDetailItemsList
        paymentId={paymentId}
        apiBasePath="payments"
        title="報酬の支払状況詳細"
        ecSiteLinkEnabled={false}
        ecSiteUseReferenceField={false}
        partnerUseReferenceField={true}
        partnerReferenceKey="affiliates"
        partnerSourceField="affiliateId"
        partnerLinkBasePath="/affiliates"
        showEcSiteColumns={false}
      />
    </Box>
  );
};

// Main payment management component
export const PartnerPaymentList: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [paymentList, setPaymentList] = useState<Payment[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useStore<number | null>('partnerpaymentlist.selectedId', null);
  const [paymentTotal, setPaymentTotal] = useState<PaymentTotal | null>(null);

  // 支払い日を選択したときの処理
  const handlePaymentSelect = async (paymentId: number) => {
    setSelectedPaymentId(paymentId);
    setSearchParams({ paymentId: paymentId.toString() });

    try {
      const totalResult = await dataProvider.getOne('payments/total', {
        id: paymentId
      });
      setPaymentTotal(totalResult.data);
    } catch (error) {
      notify('支払い合計の取得に失敗しました', { type: 'error' });
      console.error('Failed to load payment total:', error);
    }
  };

  // 初期表示時にデータを読み込むuseEffect
  useEffect(() => {
    const loadPaymentList = async () => {
      try {
        setLoading(true);

        const result = await dataProvider.getList('payments/list', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'paymentDate', order: 'DESC' },
          filter: {}
        });

        const sortedData = result.data.sort((a, b) => {
          const dateA = new Date(a.paymentDate);
          const dateB = new Date(b.paymentDate);
          return dateB.getTime() - dateA.getTime();
        });
        setPaymentList(sortedData);

        if (sortedData.length > 0) {
          const queryPaymentIdStr = searchParams.get('paymentId');
          const queryPaymentId = queryPaymentIdStr ? parseInt(queryPaymentIdStr, 10) : null;
          let idToSelect: number | null = null;

          // 優先度1: URLにpaymentIdがあり、そのIDがリストに存在すればそれを使う
          if (queryPaymentId && sortedData.some(p => p.id === queryPaymentId)) {
            idToSelect = queryPaymentId;
          }
          // 優先度2: 以前保存した選択(useStoreの値)が有効ならそれを使う
          else if (selectedPaymentId && sortedData.some(p => p.id === selectedPaymentId)) {
            idToSelect = selectedPaymentId;
          }
          // 優先度3: 上記以外ならデフォルト値（今月または最新）を使う
          else {
            const currentMonth = dayjs().format('YYYY-MM');
            const currentMonthPayment = sortedData.find((payment) => {
              const paymentMonth = dayjs(payment.paymentDate).format('YYYY-MM');
              return paymentMonth === currentMonth;
            });
            idToSelect = (currentMonthPayment || sortedData[0]).id;
          }

          if (idToSelect) {
            handlePaymentSelect(idToSelect);
          }
        }
      } catch (error) {
        notify('支払い一覧の取得に失敗しました', { type: 'error' });
        console.error('Failed to load payment list:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPaymentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ロード後、データが0件の場合
  if (paymentList.length === 0) {
    return <CustomListNoResults />;
  }

  // データが1件以上ある場合のみ、ページ全体を描画
  return (
    <Box sx={{ p: 0 }}>
      {/* Payment Schedule Section */}
      <PaymentScheduleTable
        paymentList={paymentList}
        selectedPaymentId={selectedPaymentId}
        onSelect={handlePaymentSelect}
        title="直近の支払い予定額"
      />

      {/* 支払い予定詳細セクション */}
      <>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          支払い予定詳細
        </Typography>

        {/* Total Section */}
        <PaymentTotalSummary paymentTotal={paymentTotal} />

        {/* Payment Details List - Client specific settings */}
        {selectedPaymentId && (
          <PaymentDetailsList
            paymentId={selectedPaymentId}
            paymentDate={paymentList.find(p => p.id === selectedPaymentId)?.paymentDate?.toString()}
            apiBasePath="payments"
            navigateBasePath="/partner-payments"
            showZenginExport={false}
            canEditStatus={false}
            showBulkActions={false}
            ecSiteLinkEnabled={false}
            partnerReference="affiliates"
            partnerLinkBasePath="/affiliates"
            filterPartnerReference="references/partners"
            showEcSiteFilter={false}
            showPartnerFilter={true}
            showEcSiteColumns={false}
            title={translate('resources.partner-payments.name')}
          />
        )}
      </>
    </Box>
  );
};

export default PartnerPaymentList;
